import { useEffect, useState } from "react";
import { Phone, Navigation, Zap, Truck } from "lucide-react";
import Reveal from "@/components/Reveal";
import useCallTracking from "@/hooks/useCallTracking";
import { telHref, PHONE } from "@/constants/site";
import { useT } from "@/i18n/LanguageContext";

/**
 * Real-time driver-availability status widget with a small Liverpool coverage
 * map. Sits directly under the hero so first-scroll visitors immediately see
 * "a van is available near you now".
 */
export default function DriverStatus() {
  const trackCall = useCallTracking();
  const { t } = useT();
  const [pulseTick, setPulseTick] = useState(0);

  // Subtle "just updated" indicator: bump the counter every 20s so users
  // perceive the widget as truly live.
  useEffect(() => {
    const id = setInterval(() => setPulseTick((n) => n + 1), 20000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="driver-status"
      data-testid="driver-status-section"
      className="relative bg-white dark:bg-[#0A0A0A] pt-10 sm:pt-14 pb-2 sm:pb-4"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <Reveal>
          <div
            data-testid="driver-status-card"
            className="relative overflow-hidden rounded-2xl border border-black/10 dark:border-white/10 bg-gradient-to-br from-white to-[#F5F5F7] dark:from-[#141414] dark:to-[#0A0A0A] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.35)]"
          >
            <div className="grid grid-cols-1 lg:grid-cols-5">
              {/* Left — Status */}
              <div className="lg:col-span-3 p-6 sm:p-8 lg:p-10">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0F9D58] opacity-70"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#0F9D58]"></span>
                  </span>
                  <span
                    data-testid="driver-status-text"
                    className="text-[11px] font-bold uppercase tracking-wider-2 text-[#0F9D58]"
                  >
                    {t.driverStatus.online}
                  </span>
                  <span className="ml-auto text-[10px] font-medium uppercase tracking-wider-2 text-neutral-500">
                    <span
                      key={pulseTick}
                      className="inline-block animate-pulse"
                    >
                      · {t.driverStatus.updatedNow}
                    </span>
                  </span>
                </div>

                <h3 className="font-display font-semibold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0A0A0A] dark:text-white mt-3 leading-tight">
                  {t.driverStatus.heading1}<br />
                  <span className="text-[#1E63FF]">{t.driverStatus.heading2}</span>
                </h3>
                <p className="mt-3 text-sm sm:text-base text-neutral-600 dark:text-neutral-400 max-w-md leading-relaxed">
                  {t.driverStatus.sub}
                </p>

                {/* Quick stats */}
                <div className="mt-6 grid grid-cols-2 gap-3 max-w-md">
                  <StatChip
                    icon={Zap}
                    label={t.driverStatus.etaLabel}
                    value={t.driverStatus.etaValue}
                    accent
                  />
                  <StatChip
                    icon={Navigation}
                    label={t.driverStatus.coverageLabel}
                    value={t.driverStatus.coverageValue}
                  />
                </div>

                {/* CTA */}
                <a
                  data-testid="driver-status-call-btn"
                  href={telHref}
                  onClick={trackCall("driver-status")}
                  className="mt-6 btn-blue h-12 px-6 rounded-full inline-flex items-center gap-2.5 text-sm font-semibold pulse-blue"
                >
                  <Phone className="w-4 h-4" strokeWidth={2.2} />
                  {t.driverStatus.callBtn} {PHONE}
                </a>
              </div>

              {/* Right — Stylised coverage graphic (no real map / no real address) */}
              <div
                data-testid="driver-status-map"
                className="lg:col-span-2 relative min-h-[240px] lg:min-h-[380px] bg-gradient-to-br from-[#0A0A0A] via-[#0f1729] to-[#0A0A0A] overflow-hidden"
              >
                {/* Grid overlay for a "radar / coverage" feel */}
                <div
                  className="absolute inset-0 opacity-40"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(30,99,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(30,99,255,0.18) 1px, transparent 1px)",
                    backgroundSize: "36px 36px",
                  }}
                  aria-hidden="true"
                />
                {/* Soft radial glow */}
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(30,99,255,0.25),transparent_60%)]" aria-hidden="true" />

                {/* Coverage rings */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="absolute w-24 h-24 rounded-full border border-[#1E63FF]/50" />
                  <span className="absolute w-44 h-44 rounded-full border border-[#1E63FF]/30" />
                  <span className="absolute w-64 h-64 rounded-full border border-[#1E63FF]/20" />
                  <span className="absolute w-24 h-24 rounded-full border-2 border-[#1E63FF] animate-ping opacity-40" />
                </div>

                {/* Van dots scattered across the "coverage area" */}
                <VanDot topPct={38} leftPct={30} delay="0s" />
                <VanDot topPct={62} leftPct={40} delay="0.6s" />
                <VanDot topPct={35} leftPct={68} delay="1.2s" />
                <VanDot topPct={70} leftPct={72} delay="0.3s" />

                {/* Centre van icon */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-11 h-11 rounded-full bg-[#1E63FF] border-2 border-white shadow-[0_0_20px_rgba(30,99,255,0.7)] flex items-center justify-center">
                    <Truck className="w-5 h-5 text-white" strokeWidth={2} />
                  </div>
                </div>

                {/* Area label */}
                <div className="absolute bottom-3 left-3 right-3 text-white/85 text-[11px] font-semibold uppercase tracking-wider-2 flex items-center justify-between pointer-events-none">
                  <span>{t.driverStatus.mapAreaLabel}</span>
                  <span className="text-white/50 normal-case tracking-normal">{t.driverStatus.mapCaption}</span>
                </div>

                {/* Corner badge */}
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wider-2 pointer-events-none border border-white/15">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0F9D58] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#0F9D58]"></span>
                  </span>
                  {t.driverStatus.liveMap}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StatChip({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/70 dark:bg-white/[0.03] border border-black/10 dark:border-white/10">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${accent ? "bg-[#1E63FF]" : "bg-[#0A0A0A]"} text-white`}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider-2 text-neutral-500">{label}</div>
        <div className="text-sm font-bold text-[#0A0A0A] dark:text-white truncate">{value}</div>
      </div>
    </div>
  );
}

/** Small pulsing van marker positioned via percentage props inside the coverage graphic. */
function VanDot({ topPct, leftPct, delay = "0s" }) {
  return (
    <span
      className="absolute flex items-center justify-center pointer-events-none"
      style={{ top: `${topPct}%`, left: `${leftPct}%`, transform: "translate(-50%, -50%)" }}
      aria-hidden="true"
    >
      <span className="relative flex h-3 w-3">
        <span
          className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1E63FF] opacity-70"
          style={{ animationDelay: delay }}
        />
        <span className="relative inline-flex rounded-full h-3 w-3 bg-[#1E63FF] border-2 border-white shadow" />
      </span>
    </span>
  );
}
